<template>
  <EventsControllers
    v-slot="{
      controllers: { clickSubscribeVertical, clickSubscribeHorizontal, clickSubscribeMiniPlans }
    }"
  >
    <PricesVertical
      v-if="isLayoutMatch('vertical_without_switch')"
      v-bind="{ ...commonProps, ...secondaryCommonProps }"
      :see-more="priceAreaLayout.see_more"
      :see-less="priceAreaLayout.see_less"
      :price-description-alt="priceAreaLayout.price_detail_alt"
      :background-image="priceAreaLayout.background_image"
      :prices="firstPlanArea.plans"
      @subscribe="({ price }) => clickSubscribeVertical(price)"
    />
    <PricesVerticalWithSwitch
      v-if="isLayoutMatch('vertical_with_switch')"
      v-bind="{ ...commonProps, ...secondaryCommonProps, ...switchProps }"
      :see-more="priceAreaLayout.see_more"
      :see-less="priceAreaLayout.see_less"
      :price-description-alt="priceAreaLayout.price_detail_alt"
      :background-image="priceAreaLayout.background_image"
      :left-switch-image="priceAreaLayout.left_toggle_image"
      :right-switch-image="priceAreaLayout.right_toggle_image"
      :first-plan-area-prices="firstPlanArea.plans"
      :second-plan-area-prices="secondPlanArea.plans"
      @subscribe="({ price }) => clickSubscribeVertical(price)"
    />
    <MiniPlans
      v-if="isLayoutMatch('mini_plans_without_switch')"
      v-bind="{ ...commonProps, ...secondaryCommonProps }"
      :image-url="priceAreaLayout.image.url"
      :image-alt="priceAreaLayout.image.alt"
      :prices="firstPlanArea.plans"
      @subscribe="({ price }) => clickSubscribeMiniPlans(price)"
    />
    <PricesHorizontal
      v-if="isLayoutMatch('horizontal')"
      v-bind="commonProps"
      :prices="firstPlanArea.plans"
      :quaternary-color="priceAreaLayout.quaternary_color"
      :bottom-text="priceAreaLayout.bottom_text"
      :bottom-link="priceAreaLayout.bottom_link.url || ''"
      :icon="priceAreaLayout.icon"
      :background-image="priceAreaLayout.background_image"
      @subscribe="({ price }) => clickSubscribeHorizontal(price)"
    />
    <MiniPlansWithSwitch
      v-if="isLayoutMatch('mini_plans_with_switch')"
      v-bind="{ ...commonProps, ...secondaryCommonProps, ...switchProps }"
      :image-url="priceAreaLayout.image.url"
      :image-alt="priceAreaLayout.image.alt"
      :price-area-array="[firstPlanArea.plans, secondPlanArea.plans]"
      @subscribe="({ price }) => clickSubscribeMiniPlans(price)"
    />
    <PricesVerticalWithSwitchV2
      v-if="isLayoutMatch('vertical_with_switch_v2')"
      v-bind="{
        ...commonProps,
        ...secondaryCommonProps,
        ...switchProps
      }"
      :quaternary-color="priceAreaLayout.quaternary_color"
      :quinary-color="priceAreaLayout.quinary_color"
      :senary-color="priceAreaLayout.senary_color"
      :septenary-color="priceAreaLayout.septenary_color"
      :octonary-color="priceAreaLayout.octonary_color"
      :nonary-color="priceAreaLayout.nonary_color"
      :decenary-color="priceAreaLayout.decenary_color"
      :undenary-color="priceAreaLayout.undenary_color"
      :first-plan-area-prices="firstPlanArea.plans"
      :second-plan-area-prices="secondPlanArea.plans"
      :annual-switch-discount="priceAreaLayout.annual_toggle_discount"
      :plan-highlight-text="priceAreaLayout.plan_highlight_text"
      :background-image-url="priceAreaLayout.background_image.url"
      @subscribe="({ price }) => clickSubscribeVertical(price)"
    />
    <PricesVerticalV2
      v-if="isLayoutMatch('vertical_v2')"
      v-bind="{
        ...commonProps,
        ...secondaryCommonProps
      }"
      :quaternary-color="priceAreaLayout.quaternary_color"
      :quinary-color="priceAreaLayout.quinary_color"
      :senary-color="priceAreaLayout.senary_color"
      :septenary-color="priceAreaLayout.septenary_color"
      :octonary-color="priceAreaLayout.octonary_color"
      :nonary-color="priceAreaLayout.nonary_color"
      :decenary-color="priceAreaLayout.decenary_color"
      :undenary-color="priceAreaLayout.undenary_color"
      :plan-highlight-text="priceAreaLayout.plan_highlight_text"
      :background-image-url="priceAreaLayout.background_image.url"
      :prices="firstPlanArea.plans"
      @subscribe="({ price }) => clickSubscribeVertical(price)"
    >
    </PricesVerticalV2>
  </EventsControllers>
</template>

<script>
import { mapGetters } from 'vuex';
export default {
  components: {
    MiniPlans: () => import('@/components/Prices/MiniPlans'),
    MiniPlansWithSwitch: () => import('@/components/Prices/MiniPlansWithSwitch'),
    PricesVertical: () => import('@/components/Prices/PricesVertical'),
    PricesHorizontal: () => import('@/components/Prices/PricesHorizontal'),
    PricesVerticalWithSwitch: () => import('@/components/Prices/PricesVerticalWithSwitch'),
    PricesVerticalWithSwitchV2: () => import('@/components/Prices/PricesVerticalWithSwitchV2'),
    EventsControllers: () => import('@/components/Prices/EventsControllers'),
    PricesVerticalV2: () => import('@/components/Prices/PricesVerticalV2')
  },
  props: {
    slice: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      firstPlanArea: this.defaultPlanArea(),
      secondPlanArea: this.defaultPlanArea(),
      priceAreaLayout: null,
      layoutType: null,
      isLoading: true
    };
  },
  async fetch() {
    const {
      primary: {
        layout: { lang, type, uid }
      }
    } = this.slice;
    try {
      const {
        data: { body }
      } = await this.$prismic.api.getByUID(type, uid, { lang });
      this.priceAreaLayout = body[0].primary;
      this.layoutType = body[0].slice_type;
    } catch (error) {
      console.error(error);
    }
  },
  computed: {
    ...mapGetters('plan-areas', ['parsePlan', 'getButtonText']),
    buttonText() {
      const { button_text_upgrade, button_text } = this.priceAreaLayout;
      return this.getButtonText(button_text, button_text_upgrade);
    },
    commonProps() {
      const { primary_color, secondary_color, terciary_color, button_text_free_trial } =
        this.priceAreaLayout;
      return {
        isLoading: this.isLoading,
        primaryColor: primary_color,
        secondaryColor: secondary_color,
        terciaryColor: terciary_color,
        buttonText: this.buttonText,
        buttonTextFreeTrial: button_text_free_trial || this.$t('plans.common.cards.try')
      };
    },
    secondaryCommonProps() {
      const { title, price_detail, period_text } = this.priceAreaLayout;
      return {
        title: title,
        priceDescription: price_detail,
        period: period_text
      };
    },
    switchProps() {
      const { left_toggle_text, right_toggle_text } = this.priceAreaLayout;
      return {
        leftSwitchText: left_toggle_text,
        rightSwitchText: right_toggle_text
      };
    }
  },
  async mounted() {
    await this.fetchAreaGroupPlans();
    this.isLoading = false;
  },
  methods: {
    async fetchAreaGroupPlans() {
      const { primary } = this.slice;
      try {
        const { firstPlanArea, secondPlanArea } = await this.$store.dispatch(
          'plan-areas/LOAD_PRICE_AREA_GROUP',
          primary.slug
        );
        this.firstPlanArea = firstPlanArea;
        this.secondPlanArea = secondPlanArea;
        this.firstPlanArea.plans = this.firstPlanArea.plans
          ? this.firstPlanArea.plans.map((plan) => this.parsePlan(plan))
          : [];
        this.secondPlanArea.plans = this.secondPlanArea.plans
          ? this.secondPlanArea.plans.map((plan) => this.parsePlan(plan))
          : [];
      } catch (error) {
        console.error(error);
      }
    },
    defaultPlanArea() {
      return {
        plans: [{}, {}, {}]
      };
    },
    isLayoutMatch(layoutType) {
      return this.layoutType === layoutType;
    }
  }
};
</script>
